import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from './use-auth';

/**
 * DB-backed approval status — single source of truth.
 *
 * Pre-2026-04-26 the web read `user.app_metadata.approved` from the JWT.
 * That snapshot only refreshes on re-login, so admin approval changes
 * didn't propagate until the user signed out and back in. The DB-backed
 * `user_profiles.status` is authoritative; this hook reads it via the
 * existing `GET /api/admin/me/status` endpoint (same endpoint that lazy-
 * creates the profile if missing — so first-load for any signed-in user
 * is safe).
 */
interface ApprovalStatusResponse {
  status: 'pending' | 'approved' | 'rejected';
  arc_id?: string | null;
  role?: string | null;
}

interface ApprovalStatusEnvelope {
  data: ApprovalStatusResponse;
}

export function useApprovalStatus() {
  const { user } = useAuth();

  const query = useQuery<ApprovalStatusResponse>({
    queryKey: ['admin', 'me', 'status', user?.id ?? 'anon'],
    queryFn: async () => {
      // The API wraps every response in `{ data: ... }` (sendSuccess envelope);
      // request<T> doesn't unwrap, so we have to here. Without this, query.data
      // ends up being { data: { status: 'approved' } } and `query.data.status`
      // is undefined → isApproved falls through to false → under-review.
      const envelope = await api.get<ApprovalStatusEnvelope>(
        '/api/admin/me/status'
      );
      return envelope.data;
    },
    enabled: !!user,
    staleTime: 60_000,
    // refetchOnWindowFocus removed 2026-05-27 — handled centrally by
    // <FocusResumeCoordinator> (Tier-1 immediate invalidation on tab resume).
  });

  return {
    isApproved: query.data?.status === 'approved',
    status: query.data?.status,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
